import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type MarketSettingsCostEditController from 'lumen/controllers/market/settings/cost/edit';
import type { CostModel } from 'lumen/routes/market/settings/cost';
import type { Transition } from 'lumen/services/transition';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import {
  setupUnsavedChangesTransitionHandler,
  type TransitionHandler,
} from 'lumen/utils/unsaved-changes-transition-handlers';

export type CostEditModel = CostModel & { profileId: string };

// View access and the feature toggle are gated on the parent
// `market.settings.cost` route. The edit form has no read-only mode, so
// view-only users are sent back to the list.
// @ts-expect-error: Unable to resolve signature of class decorator when called as an expression.
@protectedRoute({
  features: ['settings.cost_profiles.edit'],
  redirectTo: 'market.settings.cost',
})
export default class MarketSettingsCostEditRoute extends Route {
  @service declare router: Services['router'];

  #transitionHandler?: TransitionHandler;

  model(params: { profile_id: string }): CostEditModel {
    const model = this.modelFor('market.settings.cost') as CostModel;

    return { ...model, profileId: params.profile_id };
  }

  resetController(
    controller: MarketSettingsCostEditController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (isExiting) {
      this.#transitionHandler?.cleanup();
      this.#transitionHandler = undefined;
    }
  }

  setupController(
    controller: MarketSettingsCostEditController,
    model: CostEditModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    void controller.loadTask.perform();

    this.#transitionHandler = setupUnsavedChangesTransitionHandler(
      this.router,
      controller,
      'market.settings.cost.edit',
    );
  }
}
